/** Aligné sur ReservationForm.js (app mobile) — marge ±2h autour des missions */

export const BUSY_TIME_MARGIN_MINUTES = 120

function padTimePart(n) {
  return String(n).padStart(2, "0")
}

export function parseHHMMToMinutes(s) {
  if (s == null || s === "") return null
  const str = String(s).trim()
  if (!str) return null
  const parts = str.split(":")
  const h = parseInt(parts[0], 10)
  const m = parseInt(parts[1] ?? "0", 10)
  if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) return null
  return h * 60 + m
}

function toTimesArray(list) {
  if (Array.isArray(list)) return list
  if (list == null) return []
  if (typeof list === "string") {
    const s = list.trim()
    if (s.startsWith("{") && s.endsWith("}")) {
      const inner = s.slice(1, -1)
      if (!inner) return []
      return inner.split(",").map((x) => x.trim().replace(/^"|"$/g, ""))
    }
    try {
      const j = JSON.parse(s)
      if (Array.isArray(j)) return j
    } catch {
      /* ignore */
    }
  }
  return []
}

export function normalizeBusyDays(raw) {
  if (!raw || typeof raw !== "object") return {}
  const out = {}
  for (const [day, list] of Object.entries(raw)) {
    const rawList = toTimesArray(list)
    if (rawList.length === 0) continue
    const seen = new Set()
    const times = []
    for (const item of rawList) {
      const bm = parseHHMMToMinutes(item)
      if (bm == null) continue
      const key = `${padTimePart(Math.floor(bm / 60))}:${padTimePart(bm % 60)}`
      if (seen.has(key)) continue
      seen.add(key)
      times.push(key)
    }
    times.sort()
    out[day] = times
  }
  return out
}

export function isMinuteInBusyWindows(dayKey, minuteOfDay, busyDaysMap) {
  const times = busyDaysMap[dayKey]
  if (!times || !times.length) return false
  const maxMinute = 24 * 60 - 1
  for (const t of times) {
    const bm = parseHHMMToMinutes(t)
    if (bm == null) continue
    const lo = Math.max(0, bm - BUSY_TIME_MARGIN_MINUTES)
    const hi = Math.min(maxMinute, bm + BUSY_TIME_MARGIN_MINUTES)
    if (minuteOfDay >= lo && minuteOfDay <= hi) return true
  }
  return false
}

export function formatBusyTimesAsBlockedRanges(timesStrArr) {
  if (!Array.isArray(timesStrArr) || !timesStrArr.length) return ""
  const maxMinute = 24 * 60 - 1
  const parts = []
  for (const t of timesStrArr) {
    const bm = parseHHMMToMinutes(t)
    if (bm == null) continue
    const end = Math.min(bm + BUSY_TIME_MARGIN_MINUTES, maxMinute)
    const sh = Math.floor(bm / 60)
    const sm = bm % 60
    const eh = Math.floor(end / 60)
    const em = end % 60
    parts.push(`(${padTimePart(sh)}:${padTimePart(sm)} - ${padTimePart(eh)}:${padTimePart(em)})`)
  }
  return parts.join(", ")
}

const TIME_SLOT_STEP_MINUTES = 15

export function buildAvailableTimeSlots(dayKey, busyDaysMap) {
  const slots = []
  for (let m = 0; m < 24 * 60; m += TIME_SLOT_STEP_MINUTES) {
    if (!isMinuteInBusyWindows(dayKey, m, busyDaysMap)) {
      slots.push({
        minutes: m,
        label: `${padTimePart(Math.floor(m / 60))}:${padTimePart(m % 60)}`,
      })
    }
  }
  return slots
}

/** Message pour l’utilisateur si l’heure tombe dans une fenêtre interdite ; sinon null */
export function getBusyTimeConflictMessage(dateYmd, heureHHmm, busyDaysMap) {
  if (!dateYmd || !heureHHmm || !busyDaysMap || typeof busyDaysMap !== "object") return null
  const times = busyDaysMap[dateYmd]
  if (!times || !times.length) return null
  const m = parseHHMMToMinutes(heureHHmm)
  if (m == null) return null
  if (!isMinuteInBusyWindows(dateYmd, m, busyDaysMap)) return null
  const ranges = formatBusyTimesAsBlockedRanges(times)
  const slots = buildAvailableTimeSlots(dateYmd, busyDaysMap)
  const suggestion = slots.length > 0 ? ` Exemple de créneau libre : ${slots[0].label}.` : ""
  return ranges
    ? `Cette heure est trop proche d’une mission déjà réservée ${ranges}.${suggestion}`
    : `Cette heure entre en conflit avec l’agenda du déménageur.${suggestion}`
}
